// 화면 그리기 — 뷰어 쪽 `draw` DataChannel(§9.8.2). 채팅 `ChatChannel` 과 같은 모양:
// 능력(hello)은 세션으로 알리고, 메시지는 화면(useDrawing)이 `setListener` 로 받는다.
// 화면이 붙기 전에 온 메시지는 쌓아 두었다가 붙을 때 넘긴다(그리기를 켜기 전에 원격지가 `off` 를 보내는 등).
import { parseDrawMsg } from './protocol.ts';
import type { DrawMsg, HelloMsg } from './protocol.ts';
import type { DrawLink } from './sync.ts';

/** 세션이 아는 그리기 능력. */
export interface DrawCaps {
  /** 원격지 그림판이 있는가(`app`) — 없으면 뷰어에서만 그린다. */
  ui: 'app' | 'none';
}

/** 화면이 붙기 전 쌓아 두는 상한(그 이상은 오래된 것부터 버린다 — 켜기 전의 획은 어차피 `sync` 로 다시 온다). */
const PENDING_MAX = 500;
/** 켜기·끄기 판별을 위해 해석해 보는 메시지 최대 길이 — `on`/`off` 는 짧다(획·도형은 해석하지 않는다). */
const SHORT_MSG_MAX = 64;

export class DrawChannel implements DrawLink {
  private listener: ((m: DrawMsg) => void) | null = null;
  private pending: DrawMsg[] = [];
  capabilities: DrawCaps | null = null;
  private readonly dc: RTCDataChannel;

  constructor(dc: RTCDataChannel, onReady: (caps: DrawCaps) => void) {
    this.dc = dc;
    dc.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      const m = parseDrawMsg(ev.data);
      if (!m) return;
      if (m.t === 'hello') {
        this.capabilities = { ui: (m as HelloMsg).ui };
        onReady(this.capabilities);
        return;
      }
      if (this.listener) {
        this.listener(m);
      } else {
        this.pending.push(m);
        if (this.pending.length > PENDING_MAX) this.pending.shift();
      }
    };
  }

  get open(): boolean {
    return this.dc.readyState === 'open';
  }

  /** 이 채널로 마지막에 보낸 것이 `on` 인가(= 원격지 그림판이 떠 있다) — 세션 종료 때 끄기를 보낼지 가른다. */
  private drawingOn = false;

  send(text: string): void {
    if (this.dc.readyState !== 'open') return;
    this.dc.send(text);
    if (text.length <= SHORT_MSG_MAX) {
      const m = parseDrawMsg(text);
      if (m?.t === 'on') this.drawingOn = true;
      else if (m?.t === 'off') this.drawingOn = false;
    }
  }

  /**
   * 세션을 끝내기 직전에 부른다 — 그리기를 켜 둔 채면 원격지에 끄기를 보낸다. 반환 = 보냈는가(보냈으면 연결을 잠깐 뒤에 닫을 것).
   * 🔴 2026-09-27 실기(Mac → x64): 그리기를 끄지 않고 원격을 끝내면 원격지 그림판·막대가 **수십 초** 남았다 — 서버는 세션 종료를
   *    에이전트에 알리지 않아, 에이전트는 WebRTC 연결 점검이 `failed` 로 끝날 때까지 몰랐다.
   */
  closeRemote(): boolean {
    if (!this.drawingOn || this.dc.readyState !== 'open') return false;
    const off: DrawMsg = { t: 'off', by: 'v' };
    this.send(JSON.stringify(off));
    return true;
  }

  setListener(fn: ((m: DrawMsg) => void) | null): void {
    this.listener = fn;
    if (fn) {
      const q = this.pending;
      this.pending = [];
      for (const m of q) fn(m);
    }
  }
}
